import { ErrorKind, type NormalizedError } from './errorTypes';
import type { DiagnosticReport } from './diagnostics';
import { formatAuditTimestamp } from '@/utils/dateDisplay';

/**
 * The translation bridge.
 *
 * `src/errors/` cannot import vue-i18n — that is what keeps it usable before the app is installed and
 * testable without one. Instead `main.ts` hands us `i18n.global.t` once, and everything here resolves
 * keys through it, falling back to English if an error happens before that point.
 */

type Translator = (key: string) => string;

let translator: Translator | undefined;

/**
 * English of last resort, for errors thrown before `app.use(i18n)`.
 *
 * Only the error-kind copy needs this: every other key is rendered inside a component, where i18n is
 * guaranteed to exist by then.
 */
const FALLBACK_COPY: Record<string, string> = {
  'error_generic_title': 'Something went wrong',
  'error_generic_message': 'We hit an unexpected problem. Trying again often helps.',
  'error_network_title': 'No connection',
  'error_network_message': 'We could not reach Studypages. Check your connection and try again.',
  'error_timeout_title': 'This is taking too long',
  'error_timeout_message': 'The request timed out before Studypages answered. Please try again.',
  'error_session_title': 'Session expired',
  'error_session_message': 'Please sign in again to continue.',
  'error_forbidden_title': 'Not allowed',
  'error_forbidden_message': 'You do not have permission to do this. Ask a study administrator for access.',
  'error_not_found_title': 'Not found',
  'error_not_found_message': 'We could not find what you were looking for. It may have been moved or deleted.',
  'error_validation_title': 'Check your entries',
  'error_validation_message': 'Some information could not be accepted. Review the highlighted fields and try again.',
  'error_conflict_title': 'Already changed',
  'error_conflict_message': 'Someone else changed this while you were working. Reload to see the latest version.',
  'error_rate_limited_title': 'Too many requests',
  'error_rate_limited_message': 'Please wait a moment and try again.',
  'error_server_title': 'Studypages had a problem',
  'error_server_message': 'Something went wrong on our side. Your work was not lost — please try again.',
  'error_new_version_title': 'A new version is available',
  'error_new_version_message': 'Studypages was updated while this tab was open. Reload to continue.',
  'error_render_title': 'This screen could not load',
  'error_render_message': 'Something went wrong while displaying this page.',
  'error_route_title': 'This page could not open',
  'error_route_message': 'We could not load that screen. Try again, or go back to your studies.'
};

export const setErrorTranslator = (translate: Translator): void => {
  translator = translate;
};

export const translateErrorKey = (key: string): string => {
  try {
    const translated = translator?.(key);
    // vue-i18n echoes the key back when it is missing, which would read as gibberish to a user.
    if (translated && translated !== key) {
      return translated;
    }
  } catch {
    // Fall through to English rather than surface a translation failure as an error.
  }
  // A title slot cannot hold a sentence. Every key in `COPY` has an entry above today, so this only
  // matters for the next kind someone adds without one — which is exactly when a wrong fallback would
  // slip through unnoticed.
  const generic = key.endsWith('_title') ? 'error_generic_title' : 'error_generic_message';
  return FALLBACK_COPY[key] ?? FALLBACK_COPY[generic];
};

/**
 * Kinds where the server's own wording beats ours.
 *
 * A 403 or a validation failure knows *why* it refused; a 500 or a dropped connection produces
 * developer text ("Request failed with status code 500") that means nothing to a coordinator.
 */
const PREFER_SERVER_MESSAGE: readonly ErrorKind[] = [
  ErrorKind.EXPECTED, ErrorKind.VALIDATION, ErrorKind.FORBIDDEN,
  ErrorKind.NOT_FOUND, ErrorKind.CONFLICT, ErrorKind.RATE_LIMITED
];

export interface UserFacingError {
  title: string;
  message: string;
  reference: string;
}

/**
 * What the user actually reads.
 *
 * Never a stack, never a status code, never a URL — those live in the report. There is a test
 * asserting exactly that, because this is the boundary the whole "quiet UI" decision rests on.
 */
export const summarizeForUser = (error: NormalizedError): UserFacingError => {
  const preferServer = PREFER_SERVER_MESSAGE.includes(error.kind) && !!error.serverMessage;

  return {
    title: translateErrorKey(error.titleKey),
    message: preferServer ? error.serverMessage as string : translateErrorKey(error.messageKey),
    reference: error.reference
  };
};

export interface DetailRow {
  label: string;
  value: string;
}

/**
 * The "Show more" view: six labelled lines a support engineer can read over a screen share.
 *
 * Deliberately a different function from `formatDiagnosticReport` — this one is allowed to grow only
 * by someone deciding it should, not as a side effect of adding a field to the payload.
 */
export const formatUserDetail = (report: DiagnosticReport): DetailRow[] => {
  const rows: DetailRow[] = [
    { label: translateErrorKey('error_detail_what'), value: report.serverMessage ?? report.errorMessage ?? report.kind },
    { label: translateErrorKey('error_detail_reference'), value: report.reference },
    { label: translateErrorKey('error_detail_when'), value: formatAuditTimestamp(report.occurredAt) },
    { label: translateErrorKey('error_detail_version'), value: report.release }
  ];

  if (report.http?.status != null) {
    rows.splice(2, 0, { label: translateErrorKey('error_detail_status'), value: String(report.http.status) });
  }
  if (report.http?.path) {
    rows.splice(report.http.status != null ? 3 : 2, 0, {
      label: translateErrorKey('error_detail_request'),
      value: `${report.http.method ?? ''} ${report.http.path}`.trim()
    });
  }

  return rows;
};
